import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentUser } from '../../data/mockData';
import request from '../../utils/request';
import AccountSidebar from '../../components/AccountSidebar';
import ChangePasswordModal from '../../components/ChangePasswordModal';
import AddressModal from '../../components/AddressModal';
import CryptoJS from 'crypto-js';
import { 
  EnvelopeIcon, 
  PhoneIcon, 
  MapPinIcon,
  PencilIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const MyAccount: React.FC = () => {
  const [user, setUser] = useState(getCurrentUser());
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState<any>(null);
  const [isEditAddress, setIsEditAddress] = useState(false);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });

  // Helper function to create MD5 hash
  const createMD5Hash = (password: string): string => {
    return CryptoJS.MD5(password).toString();
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadAddresses();
  }, []);

  // 加载地址数据
  const loadAddresses = async () => {
    try {
      setIsLoadingAddresses(true);
      const response = await request({
        method: 'GET',
        url: 'addresses'
      });
      
      if (response.success) {
        setAddresses(response.result || []);
      } else {
        console.warn('Failed to load addresses:', response.message);
      }
    } catch (error) {
      console.error('Error loading addresses:', error);
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Please log in</h2>
          <Link to="/" className="hover:opacity-80" style={{ color: '#2e3a47' }}>
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  const handleSave = async () => {
    try {
      const response = await request({
        url: 'users/profile',
        method: 'PUT',
        data: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone
        }
      }); 
      if (response.success) {
        console.log('Account updated successfully');
        // 更新本地用户数据
        const updatedUser = {
          ...user,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone
        };
        setUser(updatedUser);
        
        // 更新本地存储的 currentUser
        localStorage.setItem('currentUser', JSON.stringify(updatedUser));
        
        setIsEditing(false);
        alert('Account information updated successfully!');
      } else {
        console.warn('Failed to update account:', response.message);
        alert(response.message || 'Failed to update account. Please try again.');
      }
    } catch (error) {
      console.error('Error updating account:', error);
      alert('Error updating account. Please try again.');
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone
    });
    setIsEditing(false);
  };

  const handleChangePassword = async (currentPassword: string, newPassword: string) => {
    try {
      const encryptedCurrentPassword = createMD5Hash(currentPassword);
      const encryptedNewPassword = createMD5Hash(newPassword);
      
      const response = await request({
        url: 'users/change-password',
        method: 'POST',
        data: {
          currentPassword: encryptedCurrentPassword,
          newPassword: encryptedNewPassword
        }
      });
      
      if (response.success) {
        alert('Password changed successfully!');
        setShowPasswordModal(false);
      } else {
        console.warn('Failed to change password:', response.message);
        alert(response.message || 'Failed to change password. Please try again.');
      }
    } catch (error: any) {
      console.error('Error changing password:', error);
      // enhancedRequest returns response.result directly, so error.message contains the API error message
      if (error.message) {
        alert(error.message);
      } else {
        alert('Error changing password. Please try again.');
      }
    }
  };

  const handleAddNewAddress = () => {
    setEditingAddress(null);
    setIsEditAddress(false);
    setShowAddressModal(true);
  };

  const handleEditAddress = (address: any) => {
    setEditingAddress(address);
    setIsEditAddress(true);
    setShowAddressModal(true);
  };

  const handleSaveAddress = async (addressData: any) => {
    try {
      let response;
      if (isEditAddress && editingAddress) {
        // Update existing address
        response = await request({
          url: `addresses/${editingAddress.id}`,
          method: 'PUT',
          data: addressData
        });
      } else {
        // Create new address
        response = await request({
          url: 'addresses',
          method: 'POST',
          data: addressData
        });
      }
      
      if (response.success) {
        console.log('Address saved successfully');
        alert(isEditAddress ? 'Address updated successfully!' : 'Address added successfully!');
        setShowAddressModal(false);
        // 重新加载地址列表
        loadAddresses();
      } else {
        console.warn('Failed to save address:', response.message);
        alert(response.message || 'Failed to save address. Please try again.');
      }
    } catch (error: any) {
      console.error('Error saving address:', error);
      // enhancedRequest returns response.result directly, so error.message contains the API error message
      if (error.message) {
        alert(error.message);
      } else {
        alert('Error saving address. Please try again.');
      }
    }
  };

  const handleDeleteAddress = async (addressId: number) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    try {
      const response = await request({
        url: `addresses/${addressId}`,
        method: 'DELETE'
      });
      
      if (response.success) {
        console.log('Address deleted successfully');
        alert('Address deleted successfully!');
        // 重新加载地址列表
        loadAddresses();
      } else {
        console.warn('Failed to delete address:', response.message);
        alert(response.message || 'Failed to delete address. Please try again.');
      }
    } catch (error: any) {
      console.error('Error deleting address:', error);
      // enhancedRequest returns response.result directly, so error.message contains the API error message
      if (error.message) {
        alert(error.message);
      } else {
        alert('Error deleting address. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Account</h1>
          <p className="text-gray-600">Manage your account settings and preferences</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <AccountSidebar currentPage="account" />
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
                <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                  {!isEditing ? (
                    <>
                      <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center justify-center space-x-2 px-4 py-2 hover:bg-gray-50 rounded-lg"
                        style={{ color: '#2e3a47' }}
                      >
                        <PencilIcon className="w-4 h-4" />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => setShowPasswordModal(true)}
                        className="flex items-center justify-start space-x-2 px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                      >
                        <PencilIcon className="w-4 h-4" />
                        <span>Change Password</span>
                      </button>
                    </>
                  ) : (
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                      <button
                        onClick={handleSave}
                        className="flex items-center justify-center space-x-2 px-4 py-2 text-white rounded-lg transition-colors"
                        style={{ backgroundColor: '#2e3a47' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                      >
                        <CheckIcon className="w-4 h-4" />
                        <span>Save</span>
                      </button>
                      <button
                        onClick={handleCancel}
                        className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-6">
                {/* Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                      />
                    ) : (
                      <p className="text-gray-900">{user.firstName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                      />
                    ) : (
                      <p className="text-gray-900">{user.lastName}</p>
                    )}
                  </div>
                </div>

                {/* Email and Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    {isEditing ? (
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                      />
                    ) : (
                      <div className="flex items-center space-x-2">
                        <EnvelopeIcon className="w-4 h-4 text-gray-400" />
                        <p className="text-gray-900">{user.email}</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    {isEditing ? (
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                      />
                    ) : (
                      <div className="flex items-center space-x-2">
                        <PhoneIcon className="w-4 h-4 text-gray-400" />
                        <p className="text-gray-900">{user.phone}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Addresses */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Addresses
                  </label>
                  <div className="space-y-3">
                    {isLoadingAddresses ? (
                      <div className="text-center py-4">
                        <div className="text-gray-500">Loading addresses...</div>
                      </div>
                    ) : addresses.length > 0 ? (
                      addresses.map((address, index) => (
                        <div key={address.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-3 sm:space-y-0">
                            <div className="flex-1">
                              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 mb-2">
                                <div className="flex items-center space-x-2">
                                  <MapPinIcon className="w-4 h-4 text-gray-400" />
                                  <span className="text-sm font-medium text-gray-900">
                                    {address.type.charAt(0).toUpperCase() + address.type.slice(1)} Address
                                  </span>
                                </div>
                                {address.isDefault && (
                                  <span className="px-2 py-1 text-xs rounded-full w-fit" style={{ backgroundColor: '#f0f2f5', color: '#2e3a47' }}>
                                    Default
                                  </span>
                                )}
                              </div>
                              <div className="space-y-1">
                                <p className="text-gray-900">
                                  {address.firstName} {address.lastName}
                                </p>
                                {address.company && (
                                  <p className="text-gray-600">{address.company}</p>
                                )}
                                <p className="text-gray-600">{address.addressLine1}</p>
                                {address.addressLine2 && (
                                  <p className="text-gray-600">{address.addressLine2}</p>
                                )}
                                <p className="text-gray-600">
                                  {address.city}, {address.state} {address.zipCode}
                                </p>
                                <p className="text-gray-600">{address.country}</p>
                                {address.phone && (
                                  <p className="text-gray-600">{address.phone}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                              <button 
                                onClick={() => handleEditAddress(address)}
                                className="hover:opacity-80 text-sm"
                                style={{ color: '#2e3a47' }}
                              >
                                Edit
                              </button>
                              <button 
                                onClick={() => handleDeleteAddress(address.id)}
                                className="text-red-600 hover:text-red-700 text-sm"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4">
                        <div className="text-gray-500 mb-2">No addresses found</div>
                        <div className="text-sm text-gray-400">Add your first address below</div>
                      </div>
                    )}
                    <button 
                      onClick={handleAddNewAddress}
                      className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-gray-600 hover:border-gray-400 hover:text-gray-700 text-center"
                    >
                      + Add New Address
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSubmit={handleChangePassword}
      />

      {/* Address Modal */}
      <AddressModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        onSave={handleSaveAddress}
        address={editingAddress}
        isEdit={isEditAddress}
      />
    </div>
  );
};

export default MyAccount; 
